import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import MHeader from "@/components/ui/organisms/MHeader";
import MLabGroupTile from "@/components/ui/molecules/MLabGroupTile";
import MLoader from "@/components/ui/atoms/MLoader";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useLabInsightsAllGroups } from "@/hooks/useLabInsights";
import { getMemberInfo } from "@/hooks/useMembers";
import { useScrollRestore } from "@/hooks/useScrollRestore";
import { popPathSegment } from "@/utils/path-utils";
import { LabParameterGroupId } from "@/types/lab-insights";

import styles from "./LabInsightsAllGroups.module.css";

const LabInsightsAllGroups: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const location = useLocation();
  const { data, loading, error } = useLabInsightsAllGroups(memberInfo?.id);

  // Preserve scroll position when drilling into a group and coming back, so
  // long lists don't snap to the top on return. Keyed per member so two
  // members' all-groups views restore independently.
  //
  // The "still loading" arg MUST start true and flip false only once the
  // grid is in the DOM — same contract the documents listing follows
  // (HealthMemoryList passes `!initialLoadDone || isLoading`). This hook's
  // `loading` initialises to FALSE and only flips true once fetch starts,
  // so gating on `loading` alone lets useScrollRestore run its one-shot
  // restore on the very first mount, against an empty page (scrollTop clamps
  // to 0) — and it discards the saved value before the cards mount. Adding
  // `!data` holds the restore until the groups have actually loaded.
  const { saveScroll } = useScrollRestore(
    `lab-all-groups-${memberInfo?.id ?? "unknown"}`,
    loading || !data
  );

  const groups = data?.groups ?? [];
  const centerLabel =
    memberInfo?.nickname ||
    memberInfo?.firstName ||
    t("labInsights.title");

  const handleBack = () => navigate(popPathSegment(location.pathname));
  const handleGroupClick = (groupId: LabParameterGroupId) => {
    // Save before navigating away so the return restores this scroll spot.
    saveScroll();
    navigate(`/lab-insights/groups/${groupId}`);
  };

  return (
    <div className={styles.page}>
      <MHeader
        module="labInsights"
        subHeading={t("labInsights.allTestGroups")}
        memberInfo={memberInfo}
        hideLearnMore
        onBack={handleBack}
        className={styles.flushHeader}
      />
      <MSpacer mt={4} />

      <div className={styles.centerWrap}>
        <div className={styles.centerCircle}>
          <span className={styles.centerName} title={centerLabel}>
            {centerLabel}
          </span>
        </div>
      </div>

      <div className={styles.mainSection}>
        {loading && !data && <MLoader />}

        {error && !loading && (
          <p className={styles.emptyState}>{t("labInsights.errorLoading")}</p>
        )}

        {!loading && !error && groups.length === 0 && (
          <p className={styles.emptyState}>{t("labInsights.errorLoading")}</p>
        )}

        {groups.length > 0 && (
          <div className={styles.grid}>
            {groups.map((group) => (
              <div key={group.groupId} className={styles.tileWrap}>
                <MLabGroupTile
                  group={group}
                  iconPosition="top"
                  onClick={handleGroupClick}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LabInsightsAllGroups;
